import {
  NOTE_BLOCK_NOTIFY_FIRST_SLICE,
  NOTE_BLOCK_NOTIFY_SLICE,
} from '../../config';

type Listener = () => void;

// Runs the callback in a later frame; returns what cancels it.
type FrameScheduler = (callback: () => void) => () => void;

const nextFrame: FrameScheduler = (callback) => {
  const frame = requestAnimationFrame(callback);
  return () => cancelAnimationFrame(frame);
};

// The values of a note's blocks by block id, for the widgets the document
// editor hosts. A new note replaces every entry at once, so whatever reads
// the store reads one whole note. Only the widgets whose entry changed are
// told, a slice per frame, so a note filled at once never renders every
// widget in one task.
export type NoteBlockStore<T> = {
  get: (blockId: string) => T | undefined;
  set: (entries: Record<string, T>) => void;
  subscribe: (blockId: string, listener: Listener) => () => void;
  // Drops the widgets still waiting to be told, as on unmount.
  cancel: () => void;
};

export const createNoteBlockStore = <T>({
  isSame,
  slice = NOTE_BLOCK_NOTIFY_SLICE,
  firstSlice = Math.min(slice, NOTE_BLOCK_NOTIFY_FIRST_SLICE),
  schedule = nextFrame,
}: {
  isSame: (current: T, next: T) => boolean;
  slice?: number;
  // Told at once, in the task that brought the note, which already builds
  // and patches the whole note.
  firstSlice?: number;
  schedule?: FrameScheduler;
}): NoteBlockStore<T> => {
  const state = {
    entries: {} as Record<string, T>,
    waiting: [] as string[],
    cancelFrame: null as (() => void) | null,
  };
  const listeners = new Map<string, Set<Listener>>();

  const cancel = () => {
    state.cancelFrame?.();
    state.cancelFrame = null;
    state.waiting = [];
  };

  const notifySlice = (size: number) => {
    state.cancelFrame = null;
    const now = state.waiting.slice(0, size);
    state.waiting = state.waiting.slice(size);
    if (state.waiting.length) {
      state.cancelFrame = schedule(() => notifySlice(slice));
    }
    now.forEach((blockId) =>
      listeners.get(blockId)?.forEach((listener) => listener()),
    );
  };

  const set = (next: Record<string, T>) => {
    const current = state.entries;
    // An unchanged entry keeps its object, so a widget reading it keeps
    // its snapshot.
    const merged = Object.fromEntries(
      Object.entries(next).map(([blockId, entry]) => {
        const kept = current[blockId];
        return [
          blockId,
          kept !== undefined && isSame(kept, entry) ? kept : entry,
        ];
      }),
    );
    const changed = [
      ...Object.keys(merged).filter(
        (blockId) => merged[blockId] !== current[blockId],
      ),
      ...Object.keys(current).filter((blockId) => !(blockId in merged)),
    ].filter((blockId) => listeners.has(blockId));
    state.entries = merged;
    // The widgets still waiting read this newer note when they are told.
    const waiting = [...new Set([...state.waiting, ...changed])];
    cancel();
    state.waiting = waiting;
    notifySlice(firstSlice);
  };

  const subscribe = (blockId: string, listener: Listener) => {
    const blockListeners = listeners.get(blockId) ?? new Set<Listener>();
    blockListeners.add(listener);
    listeners.set(blockId, blockListeners);
    return () => {
      blockListeners.delete(listener);
      if (!blockListeners.size) {
        listeners.delete(blockId);
      }
    };
  };

  return {
    get: (blockId) => state.entries[blockId],
    set,
    subscribe,
    cancel,
  };
};
